import { useMemo, useState } from "react";
import "./AdminPage.css";

interface PositionDraft {
    id: string
    title: string
    qualification: string
}

interface TaskDraft {
    id: string
    title: string
    date: string
    start: string
    end: string
    drawAt: string
    information: string
    positions: PositionDraft[]
}

interface FormDraft {
    surveyId: string
    surveyName: string
    tasks: TaskDraft[]
}

interface UserTokenRow {
    token: string
    name: string
    firstName: string
    lastName: string
    votes: number
    chosenVotes: number
}

const newId = () => Math.random().toString(36).substring(2, 10);

const newPosition = (title = "", qualification = ""): PositionDraft => ({
    id: newId(),
    title,
    qualification
});

const newTask = (): TaskDraft => ({
    id: newId(),
    title: "",
    date: "",
    start: "19:00",
    end: "23:00",
    drawAt: "",
    information: "",
    positions: [
        newPosition("Leiter BSW", "GF"),
        newPosition("Truppmann", "TM")
    ]
});

const initialDraft: FormDraft = {
    surveyId: "bswtheater",
    surveyName: "Brandschutzwache Theater",
    tasks: [newTask()]
};

const toApiDateTime = (value: string) => value.replace("T", " ");

const buildPayload = (draft: FormDraft) => ({
    survey: {
        id: draft.surveyId.trim(),
        name: draft.surveyName.trim()
    },
    tasks: draft.tasks.map(task => ({
        id: /^\d+$/.test(task.id) ? Number(task.id) : undefined,
        title: task.title.trim(),
        date: task.date,
        start: task.start,
        end: task.end,
        drawAt: toApiDateTime(task.drawAt),
        information: task.information,
        positions: task.positions.map(position => ({
            id: /^\d+$/.test(position.id) ? Number(position.id) : undefined,
            title: position.title.trim(),
            qualification: position.qualification.trim()
        }))
    }))
});

const validateDraft = (draft: FormDraft) => {
    const errors: string[] = [];

    if (!draft.surveyId.trim()) errors.push("Kurzlink fehlt.");
    if (!draft.surveyName.trim()) errors.push("Name der Umfrage fehlt.");
    if (draft.tasks.length === 0) errors.push("Mindestens ein Termin wird benötigt.");

    draft.tasks.forEach((task, taskIndex) => {
        const label = `Termin ${taskIndex + 1}`;
        if (!task.title.trim()) errors.push(`${label}: Titel fehlt.`);
        if (!task.date) errors.push(`${label}: Veranstaltungsdatum fehlt.`);
        if (!task.start) errors.push(`${label}: Startzeit fehlt.`);
        if (!task.end) errors.push(`${label}: Endzeit fehlt.`);
        if (!task.drawAt) errors.push(`${label}: Auslosungszeit fehlt.`);
        if (task.positions.length === 0) errors.push(`${label}: Mindestens eine Position wird benötigt.`);

        task.positions.forEach((position, positionIndex) => {
            const positionLabel = `${label}, Position ${positionIndex + 1}`;
            if (!position.title.trim()) errors.push(`${positionLabel}: Bezeichnung fehlt.`);
            if (!position.qualification.trim()) errors.push(`${positionLabel}: Qualifikation fehlt.`);
        });
    });

    return errors;
}

const AdminPage = () => {
    const [draft, setDraft] = useState<FormDraft>(initialDraft);
    const [activeTaskId, setActiveTaskId] = useState<string>(initialDraft.tasks[0].id);
    const [adminToken, setAdminToken] = useState<string>("");
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const [jsonText, setJsonText] = useState<string>("");
    const [showJson, setShowJson] = useState<boolean>(false);
    const [isLoadingSurvey, setIsLoadingSurvey] = useState<boolean>(false);
    const [userRows, setUserRows] = useState<UserTokenRow[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
    const [sourceToken, setSourceToken] = useState<string>("");
    const [targetToken, setTargetToken] = useState<string>("");

    const activeTask = draft.tasks.find(task => task.id === activeTaskId) || draft.tasks[0];
    const payload = useMemo(() => buildPayload(draft), [draft]);
    const previewJson = useMemo(() => JSON.stringify(payload, null, 2), [payload]);
    const validationErrors = useMemo(() => validateDraft(draft), [draft]);

    const updateDraft = (changes: Partial<FormDraft>) => {
        setDraft(current => ({...current, ...changes}));
    }

    const updateTask = (taskId: string, changes: Partial<TaskDraft>) => {
        setDraft(current => ({
            ...current,
            tasks: current.tasks.map(task => task.id === taskId ? {...task, ...changes} : task)
        }));
    }

    const updatePosition = (taskId: string, positionId: string, changes: Partial<PositionDraft>) => {
        setDraft(current => ({
            ...current,
            tasks: current.tasks.map(task => task.id === taskId ? {
                ...task,
                positions: task.positions.map(position => position.id === positionId ? {...position, ...changes} : position)
            } : task)
        }));
    }

    const addTask = () => {
        const task = newTask();
        setDraft(current => ({...current, tasks: [...current.tasks, task]}));
        setActiveTaskId(task.id);
    }

    const duplicateTask = (task: TaskDraft) => {
        const copy = {
            ...task,
            id: newId(),
            title: `${task.title} Kopie`,
            positions: task.positions.map(position => ({...position, id: newId()}))
        };
        setDraft(current => ({...current, tasks: [...current.tasks, copy]}));
        setActiveTaskId(copy.id);
    }

    const removeTask = (taskId: string) => {
        const tasks = draft.tasks.filter(task => task.id !== taskId);
        if (tasks.length === 0) return;
        setDraft(current => ({...current, tasks: current.tasks.filter(task => task.id !== taskId)}));
        if (activeTaskId === taskId) {
            setActiveTaskId(tasks[0].id);
        }
    }

    const addPosition = (taskId: string) => {
        setDraft(current => ({
            ...current,
            tasks: current.tasks.map(task => task.id === taskId ? {
                ...task,
                positions: [...task.positions, newPosition()]
            } : task)
        }));
    }

    const removePosition = (taskId: string, positionId: string) => {
        setDraft(current => ({
            ...current,
            tasks: current.tasks.map(task => task.id === taskId ? {
                ...task,
                positions: task.positions.filter(position => position.id !== positionId)
            } : task)
        }));
    }

    const submitPayload = (data: unknown) => {
        setIsImporting(true);
        setMessage("");
        fetch("/api/admin/import", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Admin-Token": adminToken
            },
            body: JSON.stringify(data)
        }).then(res => res.json()).then(result => {
            if (result.status === "OK") {
                setMessage(`Gespeichert: ${result.tasks.length} Termin(e) für ${result.surveyId}.`);
            } else {
                setMessage(result.message || "Das Speichern ist fehlgeschlagen.");
            }
            setIsImporting(false);
        }).catch(() => {
            setMessage("Das Speichern ist fehlgeschlagen.");
            setIsImporting(false);
        });
    }

    const saveForm = () => {
        if (validationErrors.length > 0) {
            setMessage(validationErrors[0]);
            return;
        }

        submitPayload(payload);
    }

    const loadSurvey = () => {
        if (!draft.surveyId.trim()) {
            setMessage("Kurzlink fehlt.");
            return;
        }

        setIsLoadingSurvey(true);
        setMessage("");
        fetch(`/api/admin/survey/${draft.surveyId.trim()}`, {
            headers: {
                "X-Admin-Token": adminToken
            }
        }).then(res => res.json()).then(data => {
            if (data === "unknown") {
                setMessage("Keine Umfrage mit diesem Kurzlink gefunden.");
                setIsLoadingSurvey(false);
                return;
            }

            if (data === "error" || data.status === "FORBIDDEN") {
                setMessage(data.message || "Die Umfrage konnte nicht geladen werden.");
                setIsLoadingSurvey(false);
                return;
            }

            const nextDraft: FormDraft = {
                surveyId: data.survey.id,
                surveyName: data.survey.name,
                tasks: data.tasks.map((task: any) => ({
                    id: String(task.id || newId()),
                    title: task.title || "",
                    date: task.date || "",
                    start: task.start || "",
                    end: task.end || "",
                    drawAt: task.drawAt || "",
                    information: task.information || "",
                    positions: Array.isArray(task.positions) ? task.positions.map((position: any) => ({
                        id: String(position.id || newId()),
                        title: position.title || "",
                        qualification: position.qualification || ""
                    })) : []
                }))
            };

            setDraft(nextDraft);
            setActiveTaskId(nextDraft.tasks[0]?.id || "");
            setMessage(`Umfrage geladen: ${nextDraft.tasks.length} Termin(e).`);
            setIsLoadingSurvey(false);
        }).catch(() => {
            setMessage("Die Umfrage konnte nicht geladen werden.");
            setIsLoadingSurvey(false);
        });
    }

    const loadUsers = () => {
        if (!draft.surveyId.trim()) {
            setMessage("Kurzlink fehlt.");
            return;
        }

        setIsLoadingUsers(true);
        setMessage("");
        fetch(`/api/admin/users/${draft.surveyId.trim()}`, {
            headers: {
                "X-Admin-Token": adminToken
            }
        }).then(res => res.json()).then(data => {
            if (data === "unknown") {
                setMessage("Keine Umfrage mit diesem Kurzlink gefunden.");
                setIsLoadingUsers(false);
                return;
            }

            if (data === "error" || data.status === "FORBIDDEN") {
                setMessage(data.message || "Die Personenliste konnte nicht geladen werden.");
                setIsLoadingUsers(false);
                return;
            }

            setUserRows(Array.isArray(data) ? data : []);
            setMessage(`Personen geladen: ${Array.isArray(data) ? data.length : 0} Eintrag(e).`);
            setIsLoadingUsers(false);
        }).catch(() => {
            setMessage("Die Personenliste konnte nicht geladen werden.");
            setIsLoadingUsers(false);
        });
    }

    const mergeUsers = () => {
        const source = sourceToken.trim();
        const target = targetToken.trim();
        if (!source || !target || source === target) {
            setMessage("Quell-Token und Ziel-Token müssen ausgefüllt und unterschiedlich sein.");
            return;
        }

        setIsLoadingUsers(true);
        setMessage("");
        fetch(`/api/admin/users/${draft.surveyId.trim()}/merge`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Admin-Token": adminToken
            },
            body: JSON.stringify({sourceToken: source, targetToken: target})
        }).then(res => res.json()).then(result => {
            if (result.status !== "OK") {
                setMessage(result.message || "Die Tokens konnten nicht zusammengeführt werden.");
                setIsLoadingUsers(false);
                return;
            }

            setSourceToken("");
            setTargetToken("");
            setMessage(`Zusammengeführt: ${source} wurde auf ${target} verschoben.`);
            fetch(`/api/admin/users/${draft.surveyId.trim()}`, {
                headers: {
                    "X-Admin-Token": adminToken
                }
            }).then(res => res.json()).then(data => {
                setUserRows(Array.isArray(data) ? data : []);
                setIsLoadingUsers(false);
            }).catch(() => {
                setIsLoadingUsers(false);
            });
        }).catch(() => {
            setMessage("Die Tokens konnten nicht zusammengeführt werden.");
            setIsLoadingUsers(false);
        });
    }

    const loadJsonIntoForm = () => {
        try {
            const data = JSON.parse(jsonText);
            const nextDraft: FormDraft = {
                surveyId: data.survey?.id || "",
                surveyName: data.survey?.name || "",
                tasks: Array.isArray(data.tasks) ? data.tasks.map((task: any) => ({
                    id: newId(),
                    title: task.title || "",
                    date: task.date || "",
                    start: task.start || "",
                    end: task.end || "",
                    drawAt: String(task.drawAt || "").replace(" ", "T"),
                    information: task.information || "",
                    positions: Array.isArray(task.positions) ? task.positions.map((position: any) => newPosition(position.title || "", position.qualification || "")) : []
                })) : []
            };

            setDraft(nextDraft);
            setActiveTaskId(nextDraft.tasks[0]?.id || "");
            setMessage("JSON wurde in das Formular übernommen.");
        } catch {
            setMessage("Das JSON ist nicht gültig.");
        }
    }

    return <main className="admin-page">
        <section className="admin-toolbar">
            <div>
                <h1>BSW-Verwaltung</h1>
                <p>Umfrage anlegen, Termine erfassen und Positionen zuordnen.</p>
            </div>
            <a href="/" className="admin-link">Zur Umfrage</a>
        </section>

        <section className="admin-shell">
            <div className="admin-topbar">
                <input
                    className="admin-token"
                    type="password"
                    value={adminToken}
                    onChange={(e) => setAdminToken(e.target.value)}
                    placeholder="Admin-Token"
                />
                <button className="admin-secondary" onClick={() => setShowJson(!showJson)}>
                    {showJson ? "Formular anzeigen" : "JSON anzeigen"}
                </button>
                <button className="admin-primary" onClick={saveForm} disabled={isImporting}>
                    {isImporting ? "Speichert..." : "Speichern"}
                </button>
            </div>

            {message && <div className="admin-message">{message}</div>}

            {!showJson && <>
            <div className="admin-layout">
                <aside className="admin-side">
                    <div className="admin-block">
                        <h2>Umfrage</h2>
                        <label>
                            Kurzlink
                            <input value={draft.surveyId} onChange={(e) => updateDraft({surveyId: e.target.value})} />
                        </label>
                        <button className="admin-secondary admin-load-button" onClick={loadSurvey} disabled={isLoadingSurvey}>
                            {isLoadingSurvey ? "Lädt..." : "Umfrage laden"}
                        </button>
                        <label>
                            Name
                            <input value={draft.surveyName} onChange={(e) => updateDraft({surveyName: e.target.value})} />
                        </label>
                    </div>

                    <div className="admin-block">
                        <div className="admin-row-title">
                            <h2>Termine</h2>
                            <button className="admin-icon-button" onClick={addTask} title="Termin hinzufügen">+</button>
                        </div>
                        <div className="admin-task-list">
                            {draft.tasks.map((task, index) => <button
                                key={task.id}
                                className={task.id === activeTask?.id ? "active" : ""}
                                onClick={() => setActiveTaskId(task.id)}
                            >
                                <span>{task.title || `Termin ${index + 1}`}</span>
                                <small>{task.date || "Datum offen"}</small>
                            </button>)}
                        </div>
                    </div>
                </aside>

                {activeTask && <section className="admin-editor">
                    <div className="admin-editor-header">
                        <div>
                            <h2>{activeTask.title || "Neuer Termin"}</h2>
                            <p>{activeTask.date ? `${activeTask.date} von ${activeTask.start} bis ${activeTask.end}` : "Veranstaltungsdaten erfassen"}</p>
                        </div>
                        <div className="admin-inline-actions">
                            <button className="admin-secondary" onClick={() => duplicateTask(activeTask)}>Duplizieren</button>
                            <button className="admin-danger" onClick={() => removeTask(activeTask.id)} disabled={draft.tasks.length === 1}>Löschen</button>
                        </div>
                    </div>

                    <div className="admin-grid">
                        <label>
                            Titel
                            <input value={activeTask.title} onChange={(e) => updateTask(activeTask.id, {title: e.target.value})} />
                        </label>
                        <label>
                            Veranstaltungsdatum
                            <input type="date" value={activeTask.date} onChange={(e) => updateTask(activeTask.id, {date: e.target.value})} />
                        </label>
                        <label>
                            Beginn
                            <input type="time" value={activeTask.start} onChange={(e) => updateTask(activeTask.id, {start: e.target.value})} />
                        </label>
                        <label>
                            Ende
                            <input type="time" value={activeTask.end} onChange={(e) => updateTask(activeTask.id, {end: e.target.value})} />
                        </label>
                        <label>
                            Auslosung
                            <input type="datetime-local" value={activeTask.drawAt} onChange={(e) => updateTask(activeTask.id, {drawAt: e.target.value})} />
                        </label>
                    </div>

                    <label className="admin-textarea-label">
                        Informationen
                        <textarea value={activeTask.information} onChange={(e) => updateTask(activeTask.id, {information: e.target.value})} />
                    </label>

                    <div className="admin-row-title positions-title">
                        <h2>Positionen</h2>
                        <button className="admin-secondary" onClick={() => addPosition(activeTask.id)}>Position hinzufügen</button>
                    </div>

                    <div className="admin-positions">
                        {activeTask.positions.map((position, index) => <div className="admin-position" key={position.id}>
                            <div className="admin-position-number">{index + 1}</div>
                            <label>
                                Bezeichnung
                                <input value={position.title} onChange={(e) => updatePosition(activeTask.id, position.id, {title: e.target.value})} />
                            </label>
                            <label>
                                Qualifikation
                                <input value={position.qualification} onChange={(e) => updatePosition(activeTask.id, position.id, {qualification: e.target.value})} />
                            </label>
                            <button className="admin-icon-button danger" onClick={() => removePosition(activeTask.id, position.id)} title="Position löschen">x</button>
                        </div>)}
                    </div>
                </section>}
            </div>

            <section className="admin-users-panel">
                <div className="admin-users-header">
                    <div>
                        <h2>Personen und Token</h2>
                        <p>Sortiert nach Nachname und Vorname.</p>
                    </div>
                    <button className="admin-secondary" onClick={loadUsers} disabled={isLoadingUsers}>
                        {isLoadingUsers ? "Lädt..." : "Personen laden"}
                    </button>
                </div>

                <div className="admin-merge-box">
                    <label>
                        Alter Token
                        <input value={sourceToken} onChange={(e) => setSourceToken(e.target.value)} placeholder="Token, der gelöscht wird" />
                    </label>
                    <label>
                        Richtiger Token
                        <input value={targetToken} onChange={(e) => setTargetToken(e.target.value)} placeholder="Token, der behalten wird" />
                    </label>
                    <button className="admin-primary" onClick={mergeUsers} disabled={isLoadingUsers}>
                        Zusammenführen
                    </button>
                </div>

                <div className="admin-users-table-wrap">
                    <table className="admin-users-table">
                        <thead>
                            <tr>
                                <th>Nachname</th>
                                <th>Vorname</th>
                                <th>Name</th>
                                <th>Token</th>
                                <th>Zusagen</th>
                                <th>Gewählt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {userRows.map(row => <tr key={row.token}>
                                <td>{row.lastName}</td>
                                <td>{row.firstName}</td>
                                <td>{row.name}</td>
                                <td><code>{row.token}</code></td>
                                <td>{row.votes}</td>
                                <td>{row.chosenVotes}</td>
                            </tr>)}
                            {userRows.length === 0 && <tr>
                                <td colSpan={6}>Noch keine Personen geladen.</td>
                            </tr>}
                        </tbody>
                    </table>
                </div>
            </section>
            </>}

            {showJson && <section className="admin-json">
                <div className="admin-json-panel">
                    <h2>Aktueller Stand</h2>
                    <textarea value={previewJson} readOnly spellCheck={false} />
                </div>
                <div className="admin-json-panel">
                    <h2>JSON einlesen</h2>
                    <textarea value={jsonText} onChange={(e) => setJsonText(e.target.value)} spellCheck={false} />
                    <div className="admin-json-actions">
                        <button className="admin-secondary" onClick={loadJsonIntoForm}>Ins Formular übernehmen</button>
                        <button className="admin-primary" onClick={() => {
                            try {
                                submitPayload(JSON.parse(jsonText));
                            } catch {
                                setMessage("Das JSON ist nicht gültig.");
                            }
                        }} disabled={isImporting}>JSON direkt speichern</button>
                    </div>
                </div>
            </section>}
        </section>
    </main>
}

export default AdminPage;
